import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  IconButton,
  StepIconProps,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
// import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { Box } from "@mui/system";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import spinner from "../../../../../assets/spinner.svg";
import "../Maps.css";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { useState } from "react";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";

interface EditTestingProcessModalProps {
  open: boolean;
  handleClose: () => void;
  mapNameInput: any;
  setMapNameInput: any;
}

const QontoStepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(
  ({ theme }) => ({
    color: "#eaeaf0",
    display: "flex",
    alignItems: "center",
    width: 25,
    height: 25,
    "& .QontoStepIcon-completedIcon": {
      color: "#2e7d32",
      zIndex: 1,
      width: 24,
      height: 24,
    },
    "& .QontoStepIcon-circle": {
      width: 21,
      height: 21,
      borderRadius: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.38)",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      margin: "0 auto",
    },
    "& .QontoStepIcon-errorIcon": {
      width: 24,
      height: 24,
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[700],
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          color: "#2e7d32",
        },
      },
    ],
  })
);

function QontoStepIcon(props: StepIconProps) {
  const { active, completed, error, icon, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {error ? (
        <CancelIcon className="QontoStepIcon-errorIcon" color="error" />
      ) : completed ? (
        <CheckCircleIcon
          className="QontoStepIcon-completedIcon"
          color="primary"
        />
      ) : (
        <div className="QontoStepIcon-circle">{icon}</div>
      )}
    </QontoStepIconRoot>
  );
}

const EditTestingProcessModal = ({
  open,
  handleClose,
  mapNameInput,
}: EditTestingProcessModalProps) => {
  const { mapDetailsDataPayload, sourceFieldValidationData, validating } =
    useSelector((state: any) => state.maps);
  const steps = mapDetailsDataPayload.map((item: any) => ({
    label: item.source_field_expression,
  }));

  const isStepFailed = (stepLabel: string) => {
    if (validating || !sourceFieldValidationData) return false;

    // If validation is successful, no steps are failed
    if (sourceFieldValidationData.valid) {
      return false;
    }

    // Check if this step's expression is in the missing array
    return sourceFieldValidationData.missing?.some(
      (missing: any) => missing.expression === stepLabel
    );
  };

  // Helper function to get missing brand IDs for a step
  const getMissingBrandIds = (stepLabel: string) => {
    if (!sourceFieldValidationData?.missing) return [];

    const missingItem = sourceFieldValidationData.missing.find(
      (missing: any) => missing.expression === stepLabel
    );

    return missingItem?.missing_in?.map((item: any) => item.brand_id) || [];
  };

  // Helper function to check if step is completed
  const isStepCompleted = (stepLabel: string) => {
    if (validating || !sourceFieldValidationData) return false;

    // If validation is successful, all steps are completed
    if (sourceFieldValidationData.valid) {
      return true;
    }

    // If validation failed, step is completed only if it's not in the missing array
    return !isStepFailed(stepLabel);
  };

  const [searchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const originalParams = useSelector((state: any) => state.maps.originalParams);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [removedSteps, setRemovedSteps] = useState<string[]>([]);
  const mapId: any = searchParams.get("mapId");
  const handleRemoveStep = (stepLabel: string) => {
    setRemovedSteps((prev) => [...prev, stepLabel]);
  };
  const filteredSteps = steps.filter(
    (step: any) => !removedSteps.includes(step.label)
  );
  const handleEditMap = async () => {
    setIsEditing(true);
    try {
      const filteredFieldMapping = mapDetailsDataPayload.filter(
        (item: any) => !removedSteps.includes(item.source_field_expression)
      );
      const payload = {
        map_name: mapNameInput.trim(),
        field_mapping: filteredFieldMapping,
      };
      const response: any = await MapsEndpointLogic.editMap(
        oauthTokenHeader,
        mapId,
        payload
      );
      if (response.status === 200) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Map edited successfully",
            type: "success",
          })
        );
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      }
    } catch (error: any) {
      console.error("Error editing map:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.message ||
            "Failed to edit map. Please check the details and try again.",
          type: "error",
        })
      );
    } finally {
      setIsEditing(false);
    }
  };

  const hasAnyFailedSteps = () => {
    if (validating || !sourceFieldValidationData) return false;
    if (sourceFieldValidationData.valid) return false;
    const remainingFailedSteps = sourceFieldValidationData.missing?.filter(
      (missing: any) => !removedSteps.includes(missing.expression)
    );
    return remainingFailedSteps && remainingFailedSteps.length > 0;
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleClose();
        }}
      >
        <DialogTitle>
          Testing New Source Field Expressions For All Linkages...
        </DialogTitle>
        <DialogContent>
          {validating ? (
            <Box textAlign={"center"}>
              <img width="75px" src={spinner} alt="Loading..." />
            </Box>
          ) : null}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: "10px",
              px: "10px",
              mb: "10px",
              borderBottom: "1px solid #0000001F",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              New Expression Found
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              Not Found In
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              Break Linkage
            </Typography>
          </Box>

          <Box sx={{ width: "100%" }}>
            <Stepper activeStep={-1} orientation="vertical">
              {filteredSteps?.map((step: any, _: any) => {
                const stepFailed = isStepFailed(step.label);
                const stepCompleted = isStepCompleted(step.label);
                const missingBrandIds = getMissingBrandIds(step.label);
                const labelProps: {
                  optional?: React.ReactNode;
                  error?: boolean;
                } = {};
                if (stepFailed) {
                  labelProps.optional = (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "90%",
                      }}
                    >
                      {/* <Box sx={{ flex: 1 }} />{" "} */}
                      {/* Empty space for "New Expression Found" column */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          width: "30%",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "14px" }}
                          variant="caption"
                          color="error"
                        >
                          {missingBrandIds.join(", ")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          sx={{ width: "40px", height: "40px" }}
                          onClick={() => handleRemoveStep(step.label)}
                        >
                          <LinkOffIcon fontSize="small" color="secondary" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                  labelProps.error = true;
                }

                return (
                  <Step key={step.label} completed={stepCompleted}>
                    <StepLabel
                      StepIconComponent={QontoStepIcon}
                      sx={{ padding: "0px" }}
                      {...labelProps}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleClose} color="secondary" variant="text">
            CANCEL
          </Button>

          {/* <Button variant="outlined" color="secondary">
            <ArrowBackOutlinedIcon sx={{ width: "20px", mr: "5px" }} />
            Back
          </Button> */}
          {isEditing && <SimpleBackdrop />}
          <Button
            color="secondary"
            variant="contained"
            onClick={handleEditMap}
            disabled={hasAnyFailedSteps() || isEditing || validating}
          >
            save changes
            <SaveOutlinedIcon sx={{ width: "20px", ml: "5px" }} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTestingProcessModal;
